// The COP map's interaction mode — ONE armed mode, structurally.
//
// The map used to arbitrate four armed modes through one `handleMapClick` as a
// priority chain of four independent boolean refs (`gotoPickActiveRef`,
// `pickDispatchTargetRef`, `planModeRef`, `zoneDrawModeRef`), with every
// layer-scoped handler having to stand down separately against all four.
// Nothing prevented two being armed at once; only handler ordering and the
// callers' discipline decided what a click meant, and every new mode added
// another claimant and another place to forget a guard.
//
// On a surface that commands aircraft, a click that means two things is an
// error machine. So there is now one value. Arming REPLACES — the illegal
// state is unrepresentable rather than merely avoided — and everything the map
// derives from mode (the cursor, the banner, which handlers stand down,
// Escape) reads that single value.
//
// This module is pure: no React, no MapLibre. It lives in lib/ rather than with
// the COP page because the map component and its layer builders read it too, and
// a components/ -> app/ import would be backwards. The COP page owns the VALUE;
// this file owns the vocabulary. The transition table is the thing under test.

/** Every way a click on the COP map can be claimed. `idle` is the live picture. */
export type MapMode =
  | { kind: 'idle' }
  /** "Fly here" — the next click repositions the selected aircraft. */
  | { kind: 'goto' }
  /** Quick incident dispatch — the next click is the target. */
  | { kind: 'dispatch-pick' }
  /** Drawing a geofence. The shape decides what a click contributes. */
  | { kind: 'zone-draw'; shape: 'circle' | 'polygon' }
  /** The dock launch route builder (point or route; `useDockMissionPlan` owns which). */
  | { kind: 'dock-plan' }
  /** Composing a new mission on the live map (task 3.2). */
  | { kind: 'mission-compose'; shape: MissionGeometryShape }
  /** Editing a saved mission's route in place (task 4.1). */
  | { kind: 'mission-edit'; missionId: string; shape: MissionGeometryShape };

/**
 * How a mission's geometry is collected from the map (022).
 *
 * The map carries the GEOMETRY, not the mission type. Six operator-facing types collapse to
 * two ways of clicking: HOVER, GO TO POINT, OUT & BACK, FOLLOW ROUTE and PATROL / LOOP all
 * place an ordered list of points, and only ORBIT is a centre with a radius around it. Putting
 * the type here instead would take this union from seven kinds to twelve and grow every
 * handler's stand-down logic, the cursor, the banner and Escape along with it — which is the
 * thing this module exists to prevent.
 *
 * It is also the distinction `zone-draw` already draws: a shape discriminator on one mode
 * rather than a mode per purpose. Mapbox GL Draw settles on the same split — `draw_point`,
 * `draw_line_string`, `draw_polygon`, keyed by geometry and never by what the shape means.
 *
 * 'circle' still PLACES A POINT: an orbit's centre is stored as the mission's single waypoint,
 * so it rides the same plan overlay. What differs is that the overlay draws a ring around it
 * and refuses a second point. The radius is not here — it is set from a panel control, exactly
 * as the circle geofence's is, because a briefed orbit radius is a number rather than a drag.
 */
export type MissionGeometryShape = 'waypoints' | 'circle';

export type MapModeKind = MapMode['kind'];

export const IDLE_MAP_MODE: MapMode = { kind: 'idle' };

export type MapModeAction =
  /** Arm a mode. Replaces whatever was armed — there is no "add a mode". */
  | { type: 'ARM'; mode: MapMode }
  /** Stand down whatever is armed. */
  | { type: 'DISARM' }
  /**
   * Stand down only if THIS kind is the one armed.
   *
   * For the "my workflow closed, release the map if it was still mine" case:
   * closing the dock sheet must not cancel a zone the operator started drawing
   * afterwards. A blanket DISARM there would reach across features.
   */
  | { type: 'DISARM_KIND'; kind: MapModeKind };

export function mapModeReducer(state: MapMode, action: MapModeAction): MapMode {
  switch (action.type) {
    case 'ARM':
      // Re-arming the mode already armed is a no-op by identity, so a caller
      // re-asserting its own mode on every render cannot churn the map's refs.
      if (sameMode(state, action.mode)) return state;
      return action.mode;
    case 'DISARM':
      return state.kind === 'idle' ? state : IDLE_MAP_MODE;
    case 'DISARM_KIND':
      return state.kind === action.kind ? IDLE_MAP_MODE : state;
    default:
      return state;
  }
}

/** Structural equality — the discriminant plus whatever that variant carries. */
export function sameMode(a: MapMode, b: MapMode): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'zone-draw' && b.kind === 'zone-draw') return a.shape === b.shape;
  if (a.kind === 'mission-compose' && b.kind === 'mission-compose') return a.shape === b.shape;
  if (a.kind === 'mission-edit' && b.kind === 'mission-edit') {
    // Both, not just the id: switching an open mission between a route and an orbit changes
    // what the next click means, and treating that as the same mode would leave the overlay
    // collecting the old geometry.
    return a.missionId === b.missionId && a.shape === b.shape;
  }
  return true;
}

/** Anything but the live picture. */
export function isMapModeArmed(mode: MapMode): boolean {
  return mode.kind !== 'idle';
}

/**
 * The zone shape being drawn, or null.
 *
 * The zone overlay's geometry (points, circle centre and radius) stays with the
 * zones feature; only "is the map armed for drawing, and as what" lives here,
 * because that is the part a click has to arbitrate.
 */
export function zoneDrawShape(mode: MapMode): 'circle' | 'polygon' | null {
  return mode.kind === 'zone-draw' ? mode.shape : null;
}

/**
 * The mission geometry being collected, or null when no mission mode is armed (022).
 *
 * The sibling of `zoneDrawShape`, and for the same reason: the overlay's geometry stays with
 * the missions feature, and only "is the map armed for a mission, and collecting what" lives
 * here — that being the part a click has to arbitrate.
 */
export function missionGeometryShape(mode: MapMode): MissionGeometryShape | null {
  if (mode.kind === 'mission-compose' || mode.kind === 'mission-edit') return mode.shape;
  return null;
}

/**
 * Modes whose click PLACES a point into the shared plan overlay.
 *
 * The dock launch planner and mission compose/edit all drive the same overlay
 * (`planWaypoints` / `onPlanMapClick` / `onPlanWaypointDrag`), so the map
 * treats them identically: click adds, drag moves, double-click ends point
 * entry. What differs is who owns the resulting route, which is the caller's
 * business, not the map's.
 */
export function isPlanPlacementMode(mode: MapMode): boolean {
  return mode.kind === 'dock-plan' || mode.kind === 'mission-compose' || mode.kind === 'mission-edit';
}

/**
 * Does the pointer become a crosshair?
 *
 * The rule is: if the next click is SPOKEN FOR, the pointer says so. Every
 * armed mode qualifies; only `idle` — the live picture, where a click selects —
 * leaves the pointer alone.
 *
 * 'goto' used to be the one exception, on the grounds that the fly-here flow
 * arms from a control that carries its own affordance. Manual QA proved that
 * wrong: an operator armed "Fly here", saw a map that looked exactly like the
 * live picture, and could not tell the surface was waiting on them. Of all the
 * modes it is the worst one to leave silent, because its click does not place
 * a marker — it commands an aircraft to move.
 *
 * Kept as an explicit switch rather than `isMapModeArmed` so a future variant
 * has to state its answer; the test pins the two to agree for every variant.
 */
export function mapModeWantsCrosshair(mode: MapMode): boolean {
  switch (mode.kind) {
    case 'goto':
    case 'dispatch-pick':
    case 'zone-draw':
    case 'dock-plan':
    case 'mission-compose':
    case 'mission-edit':
      return true;
    default:
      return false;
  }
}

/**
 * The banner across the top of the map: what the operator's next click will do.
 *
 * Null means no banner. The dock-plan wording depends on which kind of plan is
 * being drawn, which `useDockMissionPlan` owns, so it is passed in rather than
 * duplicated here.
 */
export function mapModeBannerLabel(
  mode: MapMode,
  dockPlanningKind: 'idle' | 'point' | 'route',
  /**
   * How many points the mode being described has collected so far.
   *
   * The banner is what the operator is actually reading while they click, so it
   * cannot describe a gesture in a state where that gesture does something else:
   * on an EMPTY compose route a double-click places the first point rather than
   * stopping (`planDoubleClickAction`), and promising otherwise here is how #641
   * would come back as a copy bug.
   */
  drawnCount = 0,
): string | null {
  switch (mode.kind) {
    case 'dock-plan':
      if (dockPlanningKind === 'point') return 'Click one target point on the map';
      if (dockPlanningKind === 'route') return 'Click route points on the map · double-click to lock';
      return null;
    // Esc and double-click both END POINT ENTRY; neither discards the route.
    // Saying "cancel" here would promise an undo that does not exist — the
    // panel's own Cancel is what throws work away, and it asks first.
    // 022: an orbit collects ONE point and it is a centre, not a destination. Saying
    // "click waypoints" there would describe an interaction the overlay refuses.
    case 'mission-compose':
      if (mode.shape === 'circle') return 'Click the centre of the orbit on the map';
      return drawnCount === 0
        ? 'Click waypoints on the map · Esc to stop placing'
        : 'Click waypoints on the map · double-click or Esc to stop placing';
    case 'mission-edit':
      return mode.shape === 'circle'
        ? 'Click to move the orbit centre · Esc to stop'
        : 'Click or drag to change the route · double-click or Esc to stop placing';
    default:
      return null;
  }
}
